import React from "react";
import { useSearchParams } from "react-router-dom";
import { Smartphone } from "lucide-react";
import { useApp } from "@/state/AppContext";

/**
 * `/{city}/checkin/:businessId?c={code}` — the QR poster/daily-code target
 * (`OWNER_APP_SPEC.md` §4.1). A phone with the Apna Shahar app installed never actually renders
 * this: the universal-link config in `apps/customer/app.config.ts` hands the OS this exact URL
 * shape straight to the app before a browser opens. This page is only ever seen when the app is
 * not installed, and it deliberately never checks anyone in itself (D8) — self check-in is
 * app-only. "Open app" re-navigates to this same URL, which is also the standard fallback for a
 * browser (commonly Android Chrome) that didn't intercept the very first tap.
 */
export default function CheckinLanding() {
  const { t } = useApp();
  const [params] = useSearchParams();
  const code = params.get("c") || "";

  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-4 px-5 py-16 text-center" data-testid="checkin-landing">
      <Smartphone className="h-10 w-10 text-primary" aria-hidden="true" />
      <h1 className="font-heading text-2xl font-bold">{t("checkin.title")}</h1>
      <p className="text-sm text-muted-foreground">{t("checkin.hint")}</p>
      {code && (
        <p className="text-xs text-muted-foreground" data-testid="checkin-code">
          {t("checkin.codeLabel")}: <span className="font-mono font-semibold">{code}</span>
        </p>
      )}
      <button
        type="button"
        onClick={() => window.location.reload()}
        data-testid="checkin-open-app-btn"
        className="mt-2 inline-flex min-h-[44px] items-center justify-center rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground"
      >
        {t("checkin.openApp")}
      </button>
      <p className="text-xs text-muted-foreground">{t("checkin.askDesk")}</p>
    </div>
  );
}
